// schema
import * as ISchema from "#webapp/src/__schema__";
// libraries
import * as React from "react";

import { track, TrackingEvents } from "#webapp/src/util/analytics.util";

import { Button } from "@clockwise/design-system";
import { AutopilotEventConflictingAttendeesDocument } from "./new-apollo-queries/__generated__/AutopilotEventConflictingAttendees.generated";
// autopilot-event-history imports
import { StatusEnum } from "#webapp/src/__schema__";
import { attendeesHaveConflict } from "#webapp/src/components/event-scheduling/EventScheduling";
import { useIntersectionObserver } from "#webapp/src/hooks";
import { unpauseEvent } from "#webapp/src/mutations";
import { setPricingDialogOpen } from "#webapp/src/state/actions/payments.actions";
import { useQuery } from "@apollo/client";
import { flexMeetingHistoryFeedbackUrl } from "@clockwise/client-commons/src/constants/site";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { AvTimer, Check, PauseCircleOutline, Warning } from "@clockwise/design-system/icons";
import { greys } from "@clockwise/web-commons/src/styles/color.styles";
import { ClockwiseHistory } from "@clockwise/web-commons/src/ui/clockwise-history";
import { EventAutopilotStatusCard } from "@clockwise/web-commons/src/ui/event-autopilot-status-card";
import { ExternalLinkButton } from "@clockwise/web-commons/src/ui/external-link-button";
import { TimelineLogType } from "@clockwise/web-commons/src/ui/timeline/timeline";
import { isElectron } from "@clockwise/web-commons/src/util/browser.util";
import {
  EventAutopilotStatusCardStatus,
  shouldRenderEventAutopilotStatus,
} from "@clockwise/web-commons/src/util/event-history.util";
import { useIsOutlook } from "@clockwise/web-commons/src/util/OutlookContext";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { debounce } from "lodash";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { getCurrentEnvironment } from "../../state/relay-environment";

/////////////////
// Styles //
/////////////////
const useStyles = makeStyles({
  container: {
    backgroundColor: greys.white,
    display: "flex",
    flexDirection: "column",
    flex: "1 0 auto",

    "& > div": {
      padding: `15px 5px 15px 15px`,
    },
  },
});

interface Props {
  eventParent: ISchema.IEventParent;
  event: ISchema.IEvent;
  scrollIntoView: boolean;
  orgId: string;
  calendarId: string;
  onViewAutopilotMeetings: () => void;
  showJoinExistingPlan?: boolean;
}

///////////////
// Component //
///////////////
const EVENT_HISTORY_ID = "cw-event-history";
const scrollSelfIntoView = (id: string) => {
  const element = document.getElementById(id);

  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "end", inline: "end" });
  }
};

const OBSERVER_INTERSECTION_THRESHOLD = 0.75;

const EventAutopilotStatusIcon: Record<EventAutopilotStatusCardStatus, React.ReactNode> = {
  CanMove: <AvTimer className="cw-w-4 cw-h-4 cw-text-positive" />,
  Paused: <Warning className="cw-w-4 cw-h-4 cw-text-warning" />,
  EventOutsideTimeRange: <Check className="cw-w-4 cw-h-4 cw-text-positive" />,
  EventExceedsDefragRange: <AvTimer className="cw-w-4 cw-h-4 cw-text-positive" />,
  TrialExpired: <PauseCircleOutline className="cw-w-4 cw-h-4 cw-text-warning" />,
  Pinned: null,
  External: null,
  NoDuration: null,
  SmartHold: null,
  PrivateEvent: null,
  OverQuota: null,
};

export const AutopilotEventHistory = ({
  eventParent,
  event,
  scrollIntoView,
  orgId,
  calendarId,
  onViewAutopilotMeetings,
  showJoinExistingPlan,
}: Props) => {
  const t = React.useContext(TranslationContext);
  const classes = useStyles();
  const dispatch = useDispatch();
  const environment = getCurrentEnvironment();

  const { data } = useQuery(AutopilotEventConflictingAttendeesDocument, {
    variables: {
      orgId,
      externalEventId: event.eventKey.externalEventId,
    },
  });
  const org = getValue(data?.org, "Org");
  const currentScheduledSlotImpact = getValue(org?.computeCurrentSlotImpactErrorable);
  const currentSlot = getValue(currentScheduledSlotImpact?.currentSlot);
  const hasConflict = attendeesHaveConflict(currentSlot);

  const eventHistory = getValue(event.eventAutopilotHistory);
  const eventAutopilotStatus = getValue(event.autopilotEventStatus)?.status || null;

  const [setTarget, entry] = useIntersectionObserver({
    threshold: OBSERVER_INTERSECTION_THRESHOLD,
  });

  const trackAutopilotHistoryEvent = useCallback(
    (trackingString: string, properties?: Record<string, unknown>) => {
      track(trackingString, {
        eventTitle: event.title,
        externalEventId: event.eventKey?.externalEventId,
        ...properties,
      });
    },
    [event],
  );

  const debouncedAPHistoryScrollTracking = debounce(
    () =>
      trackAutopilotHistoryEvent(
        TrackingEvents.AUTOPILOT_HISTORY
          .SIDEBPANEL_VIEW_FLEXIBLE_MEETING_HISTORY_SCROLL_THRESHOLD_REACHED,
      ),
    5000,
  );

  React.useEffect(() => {
    if (entry.isIntersecting) {
      debouncedAPHistoryScrollTracking();
    }
  }, [entry]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (scrollIntoView) {
      scrollSelfIntoView(EVENT_HISTORY_ID);
    }
  }, [scrollIntoView]);

  const allowViewFlexHolds = !useIsOutlook() && !isElectron;

  // hide event history only if we're missing
  // eventHistory or if the event is private
  if (!eventHistory || eventHistory.status === StatusEnum.AutopilotHistoryNotVisible) {
    return null;
  }

  const onViewPlans = () => {
    dispatch(setPricingDialogOpen(true, undefined));
  };

  const onUnpauseEvent = () => {
    unpauseEvent(
      environment,
      { orgRelayId: orgId, externalEventId: event.eventKey.externalEventId },
      () => null,
      () => null,
      calendarId,
    );

    track(TrackingEvents.DEFRAG.UNPAUSE_AUTOPILOT, {
      externalEventId: event.eventKey.externalEventId,
    });
  };

  const flexibility = getValue(eventParent.flexibility);
  const futureLogs =
    eventAutopilotStatus && shouldRenderEventAutopilotStatus(eventAutopilotStatus) && flexibility
      ? [
          {
            id: "Autopilot Status",
            description: (
              <EventAutopilotStatusCard
                status={eventAutopilotStatus}
                movableAt={flexibility.movableAt}
                movableUntil={flexibility.movableUntil}
                onUnpauseEvent={onUnpauseEvent}
                onViewPlans={onViewPlans}
                showJoinExistingPlan={showJoinExistingPlan}
              />
            ),
            defragInfo: null,
            timestamp: null,
            type: TimelineLogType.FutureLog,
            icon: EventAutopilotStatusIcon[eventAutopilotStatus],
          },
        ]
      : [];

  return (
    <div className={classes.container} id={EVENT_HISTORY_ID} ref={setTarget}>
      <ClockwiseHistory
        clockwiseHistoryStatus={eventHistory.status}
        eventAutopilotStatus={eventAutopilotStatus}
        actions={eventHistory.actions}
        trackTimelineExpanded={(payload) =>
          trackAutopilotHistoryEvent(
            TrackingEvents.AUTOPILOT_HISTORY.SIDEPANEL_FLEXIBLE_MEETING_HISTORY_SHOW_MORE,
            payload,
          )
        }
        hasConflict={hasConflict}
        futureLogs={futureLogs}
      />
      <div className="cw-flex cw-flex-col cw-gap-[16px]">
        <ExternalLinkButton
          url={flexMeetingHistoryFeedbackUrl}
          variant="outlined"
          onLinkOpen={() =>
            trackAutopilotHistoryEvent(
              TrackingEvents.AUTOPILOT_HISTORY.SIDEPANEL_FLEXIBLE_MEETING_HISTORY_GIVE_FEEDBACK,
            )
          }
          cw-id="give-feedback-button"
        >
          Give feedback
        </ExternalLinkButton>
        {allowViewFlexHolds && (
          <Button
            onClick={onViewAutopilotMeetings}
            variant="outlined"
            cw-id="flexible-meeting-view-button"
          >
            {t("ap-history:view-meetings-button")}
          </Button>
        )}
      </div>
    </div>
  );
};
